// Pagina di ritorno dopo un pagamento Stripe annullato.
// L'ordine resta in attesa; da qui si può riprendere il pagamento dello STESSO
// ordine. Prima di proporlo si legge lo stato vero dal server: un ordine pagato
// nel frattempo (altra scheda), annullato o seguito dal team non va «ripagato».
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch, ApiError } from "../api/client";
import { paymentErrorKey } from "../api/paymentError";
import { pathFor } from "../app/paths";
import { analyticsIds } from "../analytics/tracker";
import { clearCheckoutIntent, isOrderId, type PaymentState } from "../checkout/checkoutState";

interface OrderState {
  id: string;
  status: string;
  payment_state?: PaymentState;
  can_pay_online?: boolean;
}

/** Testi della pagina per gli stati in cui riprendere il pagamento non ha senso. */
const CLOSED_TEXTS: Partial<Record<PaymentState, { title: string; body: string }>> = {
  paid: { title: "checkout.cancel.titlePaid", body: "checkout.cancel.bodyPaid" },
  shipped: { title: "checkout.cancel.titlePaid", body: "checkout.cancel.bodyPaid" },
  delivered: { title: "checkout.cancel.titlePaid", body: "checkout.cancel.bodyPaid" },
  cancelled: { title: "checkout.success.titleCancelled", body: "checkout.success.bodyCancelled" },
  on_hold: { title: "checkout.success.titleOnHold", body: "checkout.success.bodyOnHold" },
  request_received: { title: "checkout.success.titleRequest", body: "checkout.success.bodyRequest" },
  processing: { title: "checkout.success.titleProcessing", body: "checkout.success.bodyProcessing" },
};

export function CheckoutCancel() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const [params] = useSearchParams();
  const rawOrderId = params.get("order_id");
  // Un order_id dall'URL entra in un percorso API solo se è un UUID (R-75).
  const orderId = isOrderId(rawOrderId) ? rawOrderId : null;

  const [order, setOrder] = useState<OrderState | null>(null);
  // Lettura dell'ordine rifiutata dal server: inesistente o di un altro account
  // (404) oppure sessione assente (401/403). Nessun pulsante di pagamento.
  const [loadError, setLoadError] = useState<"not_found" | "failed" | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    apiFetch<OrderState>(`/api/v1/shop/orders/${orderId}`)
      .then((o) => {
        if (cancelled) return;
        setOrder(o);
        const s = o.payment_state;
        if (s === "paid" || s === "shipped" || s === "delivered" || s === "cancelled") clearCheckoutIntent(o.id);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) setLoadError("not_found");
        else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) setLoadError("failed");
        /* errore di rete o 5xx: resta la pagina generica, il server decide al riprova */
      });
    return () => { cancelled = true; };
  }, [orderId]);

  const handleRetry = async () => {
    if (!orderId || retrying) return;
    setRetrying(true);
    setError("");
    try {
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        { method: "POST", body: JSON.stringify({ order_id: orderId, locale, ...analyticsIds() }) },
      );
      window.location.assign(session.checkout_url);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === "already_paid") {
        setOrder((o) => ({ ...(o ?? { id: orderId }), status: "PAID", payment_state: "paid", can_pay_online: false }));
        clearCheckoutIntent(orderId);
      } else {
        setError(t(paymentErrorKey(err)));
      }
      setRetrying(false);
    }
  };

  const state = order?.payment_state;
  const closed = state ? CLOSED_TEXTS[state] : undefined;
  // Pagamento online non disponibile per questo ordine (es. modalità manuale):
  // nessun pulsante che porterebbe solo a un errore.
  const unavailable = !closed && order?.can_pay_online === false;
  const canRetry = !!orderId && !closed && !unavailable && !loadError;

  let title: string;
  let body: string;
  if (!orderId) {
    title = t("checkout.cancel.title");
    body = rawOrderId ? t("checkout.cancel.invalidOrder") : t("checkout.cancel.body");
  } else if (loadError) {
    title = t("checkout.success.titleUnknown");
    body = loadError === "not_found" ? t("checkout.cancel.invalidOrder") : t("checkout.success.loadError");
  } else if (closed) {
    title = t(closed.title);
    body = t(closed.body);
  } else {
    title = t("checkout.cancel.title");
    body = unavailable ? t("checkout.errors.notConfigured") : t("checkout.cancel.body");
  }

  return (
    <div style={{ minHeight: "calc(100vh - 200px)", background: cfg.color_light_bg, padding: "64px 24px" }}>
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          padding: "56px 40px 40px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
          textAlign: "center",
        }}
      >
        <PausedMark color={cfg.color_accent} />

        <h1
          style={{
            fontFamily: cfg.font_heading,
            fontSize: 32,
            color: cfg.color_dark,
            margin: "28px 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h1>

        <p role="status" style={{ color: cfg.color_muted, fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
          {body}
        </p>

        {error && (
          <div
            role="alert"
            style={{
              background: "#fff5f5",
              color: "#b91c1c",
              padding: "12px 16px",
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 20,
              textAlign: "left",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          {canRetry && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              style={{
                background: cfg.color_dark,
                color: "#fff",
                border: "none",
                padding: "14px 32px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.02em",
                cursor: retrying ? "wait" : "pointer",
                opacity: retrying ? 0.7 : 1,
              }}
            >
              {retrying ? t("checkout.cancel.retrying") : t("checkout.cancel.retry")}
            </button>
          )}
          <Link
            to={`/${locale || "it"}/account`}
            style={{
              background: "transparent",
              color: cfg.color_dark,
              border: `1px solid ${cfg.color_dark}33`,
              padding: "14px 32px",
              borderRadius: 999,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {t("checkout.cancel.myOrders")}
          </Link>
        </div>

        <div style={{ marginTop: 24 }}>
          <Link
            to={pathFor("collections", locale)}
            style={{ color: cfg.color_muted, fontSize: 13, textDecoration: "none" }}
          >
            ← {t("checkout.cancel.shop")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PausedMark({ color }: { color: string }) {
  const size = 72;
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" style={{ display: "block", margin: "0 auto" }}>
      <circle cx="36" cy="36" r="34" fill={color} opacity="0.12" />
      <circle cx="36" cy="36" r="26" fill={color} />
      <rect x="29" y="25" width="4" height="22" rx="1.5" fill="#fff" />
      <rect x="39" y="25" width="4" height="22" rx="1.5" fill="#fff" />
    </svg>
  );
}
